import * as THREE from "three";
import { Sky } from "three/addons/objects/Sky.js";
import { FlyCamera } from "./flyCamera";
import { createMaterials } from "./materials";
import { buildMarina } from "./world";
import { createWater, setWaterSun, setWaterTime } from "./ocean";
import { BERTHS, DockedYacht, FLEET, buildYacht } from "./boats";
import { Habitat } from "./habitat";
import { WasteShark } from "./world/wasteshark";

export class App {
  private renderer: THREE.WebGLRenderer;
  private scene = new THREE.Scene();
  private fly: FlyCamera;
  private timer = new THREE.Timer();
  private water: THREE.Mesh;
  private boats: DockedYacht[] = [];
  private habitat: Habitat;
  private wasteShark: WasteShark;
  private sun = new THREE.Vector3();
  private caustics: THREE.Mesh | null = null;
  private burgee: THREE.Object3D | null = null;
  private dirLight: THREE.DirectionalLight;

  constructor(canvas: HTMLCanvasElement) {
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      powerPreference: "high-performance",
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 0.78;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFShadowMap;

    const camera = new THREE.PerspectiveCamera(52, window.innerWidth / window.innerHeight, 0.35, 2_000_000);
    this.fly = new FlyCamera(canvas, camera);

    this.dirLight = new THREE.DirectionalLight(0xfff1c2, 2.6);
    this.scene.add(this.dirLight);
    this.scene.add(this.dirLight.target);

    this.setupSky();
    this.setupLights();

    const mats = createMaterials();
    const marina = buildMarina(mats);
    this.scene.add(marina);
    this.caustics = marina.getObjectByName("caustics") as THREE.Mesh | null;
    this.burgee = marina.getObjectByName("burgee") ?? null;

    this.water = createWater();
    this.scene.add(this.water);
    setWaterSun(this.water, this.sun);

    for (let i = 0; i < FLEET.length; i++) {
      const spec = FLEET[i];
      const berth = BERTHS[i];
      const mesh = buildYacht(spec, mats);
      const boat = new DockedYacht(mesh, spec, berth);
      this.boats.push(boat);
      this.scene.add(mesh);
    }

    this.habitat = new Habitat();
    this.scene.add(this.habitat.group);
    this.wasteShark = new WasteShark(mats);
    this.scene.add(this.wasteShark.group);

    this.scene.fog = new THREE.FogExp2(0x8ec8da, 0.00055);
    this.scene.background = new THREE.Color(0x7eb6d4);
    this.renderer.setClearColor(0x7eb6d4, 1);
    this.timer.connect(document);

    window.addEventListener("resize", () => this.resize());
  }

  private setupSky(): void {
    const sky = new Sky();
    sky.scale.setScalar(450000);
    this.scene.add(sky);

    const elevation = 38;
    const azimuth = 42;
    const phi = THREE.MathUtils.degToRad(90 - elevation);
    const theta = THREE.MathUtils.degToRad(azimuth);
    this.sun.setFromSphericalCoords(1, phi, theta);

    const u = sky.material.uniforms;
    u["turbidity"].value = 4.5;
    u["rayleigh"].value = 3.1;
    u["mieCoefficient"].value = 0.005;
    u["mieDirectionalG"].value = 0.7;
    u["sunPosition"].value.copy(this.sun);

    this.scene.add(sky);

    const pmrem = new THREE.PMREMGenerator(this.renderer);
    const envScene = new THREE.Scene();
    envScene.background = new THREE.Color(0x9ecfe0);
    const hemiMesh = new THREE.Mesh(
      new THREE.SphereGeometry(1, 16, 12),
      new THREE.MeshBasicMaterial({ color: 0xd8eef6, side: THREE.BackSide }),
    );
    envScene.add(hemiMesh);
    const env = pmrem.fromScene(envScene, 0.02).texture;
    this.scene.environment = env;
    this.scene.environmentIntensity = 0.85;
    envScene.remove(hemiMesh);
    hemiMesh.geometry.dispose();
    (hemiMesh.material as THREE.Material).dispose();
    pmrem.dispose();
  }

  private setupLights(): void {
    this.dirLight.position.copy(this.sun).multiplyScalar(180);
    this.dirLight.target.position.set(0, 0, 0);
    this.dirLight.castShadow = true;
    this.dirLight.shadow.mapSize.set(2048, 2048);
    this.dirLight.shadow.bias = -0.0012;
    this.dirLight.shadow.normalBias = 0.08;
    const cam = this.dirLight.shadow.camera;
    cam.near = 1;
    cam.far = 520;
    cam.left = -200;
    cam.right = 200;
    cam.top = 220;
    cam.bottom = -260;
    cam.updateProjectionMatrix();

    this.scene.add(new THREE.AmbientLight(0xb9d4de, 0.7));
    const hemi = new THREE.HemisphereLight(0xc5e7f2, 0x7a6a4e, 1.35);
    this.scene.add(hemi);

    const fill = new THREE.DirectionalLight(0x9ad7e3, 0.7);
    fill.position.set(-40, 30, -20);
    this.scene.add(fill);
  }

  private resize(): void {
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.fly.camera.aspect = w / h;
    this.fly.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h);
  }

  start(): void {
    this.renderer.setAnimationLoop(() => this.tick());
  }

  private tick(): void {
    this.timer.update();
    const dt = Math.min(this.timer.getDelta(), 0.05);
    const t = this.timer.getElapsed();
    this.fly.update(dt);
    setWaterTime(this.water, t);
    for (const boat of this.boats) boat.update(t, dt);
    this.habitat.update(t, dt);
    this.wasteShark.update(t, dt);

    if (this.caustics) {
      const mat = this.caustics.material as THREE.MeshBasicMaterial;
      if (mat.map) {
        mat.map.offset.set(t * 0.03, t * 0.02);
      }
    }
    if (this.burgee) {
      this.burgee.rotation.y = Math.sin(t * 2.1) * 0.22;
    }

    this.renderer.render(this.scene, this.fly.camera);
  }
}
